import { useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Search } from "lucide-react";
import { api, asset, formatApiError } from "@/lib/api";
import { field, Label } from "../formBits";
import { SourceChip, trackKey, newTrack } from "./trackBits";

const Row = ({ t, added, onAdd, k }) => (
  <li className="flex items-center gap-3 px-3 py-2 border-b border-white/5 last:border-0">
    <img src={asset(t.cover)} alt="" loading="lazy" className="w-9 h-9 object-cover shrink-0 bg-black" />
    <span className="min-w-0 flex-1">
      <span className="block text-sm text-zinc-200 truncate">{t.title}</span>
      <span className="block text-xs text-zinc-400 truncate">{t.artist}</span>
    </span>
    <button type="button" data-testid={`music-source-add-${k}`} onClick={onAdd} disabled={added} aria-label={added ? "Already in queue" : "Add to queue"}
      className={`shrink-0 w-9 h-9 grid place-items-center border transition-colors ${added ? "border-[#F5A524]/20 text-[#F5A524]/60" : "border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524] hover:text-black"}`}>
      {added ? <Check size={14} /> : <Plus size={14} />}
    </button>
  </li>
);

export const SourceBrowser = ({ url, setUrl, queue, onAdd }) => {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const inQueue = new Set(queue.map(trackKey));

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/music/source", { params: { url } });
      setSrc(data);
    } catch (err) {
      setSrc(null);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fresh = (src?.tracks ?? []).filter((t) => !inQueue.has(trackKey(t)));
  return (
    <div className="space-y-3" data-testid="music-source-browser">
      <Label>PASTE A SPOTIFY OR YOUTUBE MUSIC LINK (PLAYLIST, ALBUM OR SONG)</Label>
      <div className="flex gap-2">
        <input data-testid="music-url-input" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); load(); } }}
          placeholder="https://music.youtube.com/playlist?list=...  or  https://open.spotify.com/playlist/..." className={field} />
        <button type="button" data-testid="music-load-btn" onClick={load} disabled={busy || !url.trim()}
          className="shrink-0 flex items-center gap-2 px-4 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] hover:bg-white disabled:opacity-50">
          <Search size={13} /> {busy ? "LOADING..." : "LOAD"}
        </button>
      </div>
      {src && (
        <div data-testid="music-source-results" className="bg-[#0B0C10] border border-[#F5A524]/20">
          <div className="flex items-center gap-3 p-3 border-b border-[#F5A524]/20">
            {src.cover && <img src={asset(src.cover)} alt="" className="w-12 h-12 object-cover" />}
            <div className="min-w-0 flex-1">
              <p data-testid="music-source-name" className="font-display font-bold uppercase text-lg truncate">{src.name}</p>
              <p className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-zinc-400"><SourceChip t={src.tracks[0]} /> {src.tracks.length} TRACKS</p>
            </div>
            <button type="button" data-testid="music-source-add-all" onClick={() => onAdd(fresh.map(newTrack))} disabled={!fresh.length}
              className="shrink-0 px-3 py-2 border border-[#F5A524]/50 text-[#F5A524] font-mono text-[11px] tracking-[0.2em] hover:bg-[#F5A524] hover:text-black disabled:opacity-40">
              ADD ALL ({fresh.length})
            </button>
          </div>
          <ul data-lenis-prevent className="max-h-72 overflow-y-auto">
            {src.tracks.map((t, k) => <Row key={trackKey(t) + k} k={k} t={t} added={inQueue.has(trackKey(t))} onAdd={() => onAdd([newTrack(t)])} />)}
          </ul>
        </div>
      )}
    </div>
  );
};
